<template>
	<a-modal
		class="g-image-grid-edit-modal"
		:visible="visible"
		title="图片模块编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-image-grid-edit" @click="clearSelection">
			<div class="g-image-grid-edit__actions" @click.stop>
				<span v-if="form.items.length > 1" class="g-image-grid-edit__hint">图片支持拖拽排序</span>
				<div class="g-image-grid-edit__actions-btns">
					<button type="button" class="g-image-grid-edit__batch-btn" @click="addBatch">添加一批</button>
					<button type="button" class="g-image-grid-edit__batch-btn" @click="addOne">添加一格</button>
				</div>
			</div>

			<EditListSortGroup
				ref="editGrid"
				class="g-image-grid-edit__grid"
				@dragover="onSortDragOver"
			>
				<div
					v-for="(item, index) in form.items"
					:key="item.id"
					:data-sort-id="item.id"
					class="g-image-grid-edit__cell-wrap"
					:class="[{ 'is-active': selectedIndex === index }, getEditListSortItemClass(dragId, item.id, dragOverId)]"
					@click.stop
				>
					<div
						class="g-image-grid-edit__cell"
						:class="{ 'is-selected': selectedIndex === index }"
						:draggable="form.items.length > 1"
						@click="selectItem(index)"
						@dragstart="onItemDragStart(item.id)"
						@dragend="onItemDragEnd"
					>
						<div
							class="g-image-grid-edit__thumb"
							:style="previewThumbStyle"
						>
							<UploadProgressPanel
								v-if="isUploading && selectedIndex === index"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
							/>
							<template v-else>
								<img v-if="getImageUrl(item)" :src="getImageUrl(item)" alt="" />
								<i v-else class="mbm-iconfont mbm-tupian"></i>
							</template>
						</div>
						<input
							v-model="item.title"
							type="text"
							class="g-image-grid-edit__title-input"
							:style="{ color: form.textColor }"
							maxlength="40"
							placeholder="图片标题"
							@click.stop="selectItem(index)"
							@focus="selectItem(index)"
						/>
					</div>

					<div
						v-if="selectedIndex === index"
						class="g-image-grid-edit__panel g-image-grid-edit__panel--arrow g-image-grid-edit__panel--float"
						:class="getPanelAlignClass(index)"
						@click.stop
					>
						<div class="g-image-grid-edit__toolbar">
							<button type="button" class="g-image-grid-edit__action" @click="triggerImageSelect">换图</button>
							<button
								type="button"
								class="g-image-grid-edit__action"
								:class="{ 'is-active': activePanel === 'height' }"
								@click="togglePanel('height')"
							>高度</button>
							<button
								type="button"
								class="g-image-grid-edit__action"
								:class="{ 'is-active': activePanel === 'radius' }"
								@click="togglePanel('radius')"
							>圆角</button>
							<button
								type="button"
								class="g-image-grid-edit__action g-image-grid-edit__action--danger-text"
								:disabled="form.items.length <= 1"
								@click="deleteItem(index)"
							>删除</button>
							<div class="g-image-grid-edit__colors">
								<ColorPicker
									v-model="form.textColor"
									title="文字颜色"
									@change="onTextColorInput"
								/>
							</div>
						</div>
						<div v-if="activePanel === 'height'" class="g-image-grid-edit__sub-row">
							<span class="g-image-grid-edit__sub-label">高度 (px)</span>
							<a-input-number
								v-model:value="heightDraft"
								:min="1"
								:max="800"
								:precision="0"
								class="g-image-grid-edit__sub-input"
							/>
							<button type="button" class="g-image-grid-edit__sub-confirm" @click="applyHeight">确定</button>
							<span class="g-image-grid-edit__sub-default">默认值:{{ defaultHeight }}</span>
						</div>
						<div v-if="activePanel === 'radius'" class="g-image-grid-edit__sub-row">
							<span class="g-image-grid-edit__sub-label">圆角 (px)</span>
							<a-input-number
								v-model:value="radiusDraft"
								:min="0"
								:max="100"
								:precision="0"
								class="g-image-grid-edit__sub-input"
							/>
							<button type="button" class="g-image-grid-edit__sub-confirm" @click="applyRadius">确定</button>
						</div>
					</div>
				</div>
			</EditListSortGroup>
		</div>
		</div>

		<input
			ref="imageInput"
			type="file"
			accept="image/*"
			class="g-image-grid-edit__file-input"
			@change="onImageSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-image-grid-edit-modal__ok" :disabled="isUploading" @click="onConfirm">确定</a-button>
		</template>
	</a-modal>
</template>

<script>
	import ColorPicker from './ColorPicker.vue';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		createDefaultImageGridItem,
		createDefaultImageGridItems,
		getImageGridImageUrl,
		getImageGridThumbAspectRatio,
		IMAGE_GRID_DEFAULT_HEIGHT,
		normalizeImageGridData,
	} from '../utils/imageGrid.js';
	import { ensureEditFloatPanelVisible, getGridPanelAlignClass } from '../utils/editPanelAlign.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import {
		createEditListDragState,
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
		resolveSelectedIndexAfterListMove,
	} from '../utils/editListDragSort.js';
	import { getSwatchBorderColor, normalizeHexColor } from '../utils/richText.js';
	import { replaceQrcodeNavCellImage } from '../utils/qrcodeNav.js';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GImageGridEditModal',
		components: { EditListSortGroup, UploadProgressPanel, ColorPicker },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeImageGridData({}),
				selectedIndex: null,
				activePanel: '',
				heightDraft: IMAGE_GRID_DEFAULT_HEIGHT,
				radiusDraft: 0,
				defaultHeight: IMAGE_GRID_DEFAULT_HEIGHT,
				isUploading: false,
				uploadTasks: [],
				...createEditListDragState(),
			};
		},
		computed: {
			selectedItem() {
				return this.form.items[this.selectedIndex] || null;
			},
			previewThumbStyle() {
				return {
					aspectRatio: getImageGridThumbAspectRatio(this.form.imageHeight),
					borderRadius: `${this.form.borderRadius}px`,
				};
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selectedIndex = null;
					this.activePanel = '';
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			getEditListSortItemClass,
			getPanelAlignClass(index) {
				return getGridPanelAlignClass(index, 3);
			},
			ensureActivePanelVisible() {
				ensureEditFloatPanelVisible(this, {
					wrapSelector: '.g-image-grid-edit__cell-wrap',
					panelSelector: '.g-image-grid-edit__panel--float',
				});
			},
			normalizeColor(color) {
				return normalizeHexColor(color);
			},
			getImageUrl(item) {
				return getImageGridImageUrl(item?.image);
			},
			getSwatchStyle(color) {
				const normalized = normalizeHexColor(color);
				return {
					background: normalized,
					borderColor: getSwatchBorderColor(normalized),
				};
			},
			syncForm() {
				this.form = normalizeImageGridData(this.data);
				this.heightDraft = this.form.imageHeight;
				this.radiusDraft = this.form.borderRadius;
				if (
					this.selectedIndex != null
					&& this.selectedIndex >= this.form.items.length
				) {
					this.selectedIndex = null;
				}
			},
			selectItem(index) {
				this.selectedIndex = index;
				this.ensureActivePanelVisible();
			},
			clearSelection() {
				this.selectedIndex = null;
				this.activePanel = '';
			},
			togglePanel(panel) {
				this.activePanel = this.activePanel === panel ? '' : panel;
				if (panel === 'height') {
					this.heightDraft = this.form.imageHeight;
				}
				if (panel === 'radius') {
					this.radiusDraft = this.form.borderRadius;
				}
				if (this.activePanel) {
					this.ensureActivePanelVisible();
				}
			},
			addBatch() {
				this.form.items.push(...createDefaultImageGridItems(3));
				this.selectedIndex = this.form.items.length - 3;
			},
			addOne() {
				this.form.items.push(createDefaultImageGridItem({}, this.form.items.length));
				this.selectedIndex = this.form.items.length - 1;
			},
			onItemDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.editGrid, this);
			},
			onItemDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.editGrid, this.form.items, this, (result) => {
					this.selectedIndex = resolveSelectedIndexAfterListMove(
						this.selectedIndex,
						result.fromIndex,
						result.toIndex,
					);
				});
			},
			deleteItem(index) {
				if (this.form.items.length <= 1) return;
				const removed = this.form.items[index];
				if (removed?.image?.local) {
					revokeLocalImageUrl(removed.image.previewUrl);
				}
				this.form.items.splice(index, 1);
				if (this.selectedIndex === index) {
					this.selectedIndex = null;
					this.activePanel = '';
				} else if (this.selectedIndex != null && this.selectedIndex > index) {
					this.selectedIndex -= 1;
				}
			},
			triggerImageSelect() {
				this.$refs.imageInput?.click();
			},
			async onImageSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || !this.selectedItem) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.selectedItem.image = replaceQrcodeNavCellImage(
						this.selectedItem.image,
						file,
						() => item,
					);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onTextColorInput(color) {
				this.form.textColor = color;
			},
			applyHeight() {
				this.form.imageHeight = Math.max(1, Number(this.heightDraft) || IMAGE_GRID_DEFAULT_HEIGHT);
				this.activePanel = '';
			},
			applyRadius() {
				this.form.borderRadius = Math.max(0, Number(this.radiusDraft) || 0);
				this.activePanel = '';
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeImageGridData({
					...this.form,
					items: this.form.items.map(item => ({
						...item,
						title: item.title?.trim() || '图片标题',
					})),
				});
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-image-grid-edit__panel);
	.edit-float-toolbar(g-image-grid-edit__toolbar);
	.edit-float-action(g-image-grid-edit__action);

	.g-image-grid-edit {
		min-height: 360px;
		padding: 4px 0 12px;
	}

	.g-image-grid-edit__actions {
		.edit-list-sort-actions-row();
		margin-bottom: 16px;
	}

	.g-image-grid-edit__actions-btns {
		.edit-list-sort-actions-btns();
	}

	.g-image-grid-edit__batch-btn {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;

		&:hover {
			color: #1d4ed8;
		}
	}

	.g-image-grid-edit__grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		width: 100%;
		max-width: 520px;
		margin: 0 auto;
		overflow: visible; 
	}

	.g-image-grid-edit__hint {
		.edit-list-sort-hint-text();
	}

	.g-image-grid-edit__cell-wrap {
		position: relative;
		z-index: 0;
		overflow: visible;

		&.is-active {
			z-index: 2;
		}
	}

	.g-image-grid-edit__cell {
		box-sizing: border-box;
		border: 2px dashed #fff;
		border-radius: 2px;
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&.is-selected {
			border-color: #285af9;
		}
	}

	.g-image-grid-edit__thumb {
		width: 100%;
		height: auto;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 24px;
			color: #bfbfbf;
		}
	}

	.g-image-grid-edit__title-input {
		display: block;
		width: 100%;
		margin-top: 8px;
		border: none;
		outline: none;
		background: transparent;
		font-size: 14px;
		font-weight: 600;
		font-family: inherit;
		padding: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.g-image-grid-edit__colors {
		display: inline-flex;
		align-items: center;
		margin-left: auto;
		padding-left: 8px;
	}

	.g-image-grid-edit__swatch {
		position: relative;
		display: inline-block;
		width: 18px;
		height: 18px;
		border: 1px solid #b0b0b0;
		border-radius: 3px;
		cursor: pointer;
		overflow: hidden;
		flex-shrink: 0;
		box-sizing: border-box;

		&:hover {
			box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.25);
		}
	}

	.g-image-grid-edit__color-input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		padding: 0;
		border: none;
		opacity: 0;
		cursor: pointer;
	}

	.g-image-grid-edit__sub-row {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		gap: 8px;
		padding: 8px 12px 10px;
		border-top: 1px solid #f0f0f0;
		white-space: nowrap;
	}

	.g-image-grid-edit__sub-label {
		font-size: 13px;
		color: #666;
		white-space: nowrap;
		flex-shrink: 0;
	}

	.g-image-grid-edit__sub-input {
		width: 88px;
		flex-shrink: 0;
	}

	.g-image-grid-edit__sub-confirm {
		flex-shrink: 0;
		height: 28px;
		padding: 0 12px;
		border: none;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 12px;
		line-height: 28px;
		white-space: nowrap;
		cursor: pointer;

		&:hover {
			background: #3868ff;
		}
	}

	.g-image-grid-edit__sub-default {
		font-size: 12px;
		color: #999;
		white-space: nowrap;
		flex-shrink: 0;
	}

	.g-image-grid-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-image-grid-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();

		.g-image-grid-edit-modal__ok {
			

			&:hover,
			&:focus {
				background: #3868ff;
				border-color: #3868ff;
			}
		}
	}
</style>
